import { useQuery } from "@apollo/client";
import { InfoCircle } from "@untitled-ui/icons-react";
import { uniqBy } from "lodash";
import { useMemo, useState } from "react";
import {
  GetProfileCareerQueryQuery,
  GetProfileCareerQueryQueryVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, isAdminVar } from "@cache/cache";
import Button, { buttonTheme } from "@components/button/button";
import Loading from "@components/loading/loading";
import { Select, SelectOption } from "@components/select/select";
import useUrlQueryParams from "@helpers/hooks/use-url-query-params";
import { titleCase } from "@helpers/string";

import ProfileCareerTable from "./components/career-table";
import getProfileCareerQuery from "./graphql/get-profile-career-query";

const ProfileCareerTab = ({ profileId }: { profileId: number }) => {
  const label = useLabel();
  const queryParams = useUrlQueryParams<{
    careerTrackId: number | null;
  }>({
    careerTrackId: null,
  });
  const currentOrganization = currentOrganizationVar();
  const { data, loading } = useQuery<
    GetProfileCareerQueryQuery,
    GetProfileCareerQueryQueryVariables
  >(getProfileCareerQuery, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    variables: { userId: profileId, organizationId: currentOrganization.id },
  });

  const profile = data?.user;
  const orgCareerTracksCount = data?.careerTracks.totalCount ?? 0;
  const roles = useMemo(() => profile?.currentRoles || [], [profile]);
  const tracks = useMemo(
    () =>
      uniqBy(
        roles.map((role) => role.careerTrack),
        "id"
      ).filter(Boolean),
    [roles]
  );
  const userRolesIds = useMemo(() => roles.map((role) => role.id), [roles]);
  const careerTrackOptions = useMemo(() => {
    return tracks.length > 0
      ? tracks.map((track) => ({
          label: track.title,
          value: track.id,
        }))
      : [{ label: "Select a career track...", value: 0 }];
  }, [tracks]);

  const suggestedTrackId = careerTrackOptions[0]?.value;
  const [_selectedCareerTrackId, setSelectedCareerTrackId] = useState<
    number | null
  >(Number(queryParams.careerTrackId) ?? null);
  const selectedCareerTrackId = tracks.some(
    (track) => track.id === _selectedCareerTrackId
  )
    ? _selectedCareerTrackId
    : suggestedTrackId;
  const selectedTrack = tracks.find(
    (track) => track.id === selectedCareerTrackId
  );

  const handleCareerTrackChange = (option: SelectOption<number>) => {
    setSelectedCareerTrackId(option.value);
  };

  const isAdmin = isAdminVar();

  return (
    <div className="flex flex-col gap-6">
      {loading ? (
        <Loading />
      ) : !profile ? (
        <div className="text-gray-500 text-sm text-center">
          No profile found.
        </div>
      ) : (
        <div>
          <div className="flex gap-4 items-center text-gray-500">
            My Career Track(s)
            <Select<number>
              options={careerTrackOptions}
              value={selectedCareerTrackId ?? 0}
              onChange={handleCareerTrackChange}
              disabled={tracks.length === 0}
            />
          </div>
          {tracks.length === 0 ? (
            <div className="mt-6 text-gray-800 text-sm bg-gray-100 rounded-lg p-6 flex gap-4">
              <InfoCircle className="w-5 h-5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">
                  {isAdmin && orgCareerTracksCount === 0
                    ? titleCase(
                        `No Tracks Setup In Your ${label("organization")}`
                      )
                    : titleCase("No Tracks Setup")}
                </div>
                <div className="mt-1">
                  {isAdmin && orgCareerTracksCount === 0
                    ? `No ${label(
                        "career"
                      )} responsibilities or roles are setup in your ${label(
                        "organization"
                      )}. In order to use this feature please set them up.`
                    : isAdmin
                    ? `No ${label(
                        "career"
                      )} responsibilities or roles are setup for you. In order to use this feature please set them up.`
                    : `No ${label(
                        "career"
                      )} responsibilities or roles are setup for you. In order to use this feature please contact an administrator to get them setup.`}
                </div>
                {isAdmin && (
                  <div className="mt-4">
                    <Button
                      theme={buttonTheme.primary}
                      text="Edit Roles and Competencies"
                      to={`/settings/organization/${currentOrganization.id}/competencies`}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : selectedTrack ? (
            <ProfileCareerTable
              key={selectedCareerTrackId}
              profile={profile}
              userRolesIds={userRolesIds}
              selectedCareerTrack={selectedTrack}
            />
          ) : (
            <div className="text-gray-500 text-sm text-center">
              No career track selected.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileCareerTab;
